<template>
  <v-navigation-drawer
    v-model="drawer"
    :right="$vuetify.rtl"
    fixed
    temporary
    hide-overlay
    width="310"
    class="sidemenu-2027"
    :class="{ 'sidemenu-2027--dark': $vuetify.theme.dark }"
  >
    <!-- Animated aurora blob -->
    <div class="aurora-blob aurora-blob--2" />

    <div class="menu-shell">
      <!-- ───── HEADER ───── -->
      <div class="menu-header">
        <div class="user-block">
          <div class="user-avatar">{{ userInitials }}</div>
          <div class="user-info">
            <div class="user-name">{{ userName }}</div>
            <div class="user-sub">{{ businessName }}</div>
          </div>
        </div>
        <button type="button" class="close-pill" @click="drawer = false">
          <v-icon size="16">mdi-close</v-icon>
        </button>
      </div>

      <!-- ───── BODY ───── -->
      <div class="menu-body">
        <div
          v-for="section in groupedItems"
          :key="section.key"
          class="menu-section"
        >
          <div class="section-chip">
            <span class="section-chip-dot" />
            {{ section.title }}
          </div>

          <button
            v-for="(item, idx) in section.items"
            :key="item.to || item.title"
            type="button"
            class="menu-item"
            :class="{
              'menu-item--active': isActive(item),
              'menu-item--danger': item.isDanger,
            }"
            :style="{ transitionDelay: idx * 30 + 'ms' }"
            @click="item.to ? goTo(item.to) : item.method()"
          >
            <span class="item-icon-wrap">
              <Icon :icon="item.icon" />
            </span>
            <span class="item-label">{{ item.title }}</span>
            <span v-if="isActive(item)" class="item-active-bar" />
          </button>
        </div>
      </div>

      <!-- ───── FOOTER ───── -->
      <div class="menu-footer">
        <div class="locale-glass">
          <Locale />
        </div>
      </div>
    </div>

    <Modal :activator="u_logout" @close="u_logout = false" />
  </v-navigation-drawer>
</template>

<script>
import Locale from "@/components/general/locale.vue";
import lang from "@/helpers/lang";
import { hasSolarActionsDevice } from "@/helpers/solarActionsAccess";
import Modal from "../general/LogOutModal.vue";

export default {
  components: { Locale, Modal },
  data() {
    return {
      u_logout: false,
    };
  },
  computed: {
    drawer: {
      get() {
        return this.$store.getters["general/sideMenu"];
      },
      set(status) {
        this.$store.dispatch("general/setSideMenu", status);
      },
    },
    userName() {
      return this.$store.getters["user/name"];
    },
    userInitials() {
      const parts = String(this.userName || "")
        .trim()
        .split(" ")
        .filter(Boolean);
      if (!parts.length) return "U";
      return parts
        .slice(0, 2)
        .map((p) => (p[0] || "").toUpperCase())
        .join("");
    },
    path() {
      return this.$route.path;
    },
    hasGroups() {
      return this.$store.getters["groups/hasGroups"];
    },
    businessName() {
      const b = this.$store.getters["business/business"];
      if (!b) return "SwitchQ Workspace";
      return b.custom ? b.title : b.name || "SwitchQ Workspace";
    },
    hasSolarActionsDevices() {
      return hasSolarActionsDevice(this.$store.getters["devices/alldevices"]);
    },
    isKioskSession() {
      return this.$store.getters["user/isKioskSession"] || this.$isKioskSession;
    },
    items() {
      return [
        {
          icon: "mdiHome",
          title: this.$t("links.home"),
          to: "/dashboard",
          valid: true,
          section: "main",
        },
        {
          icon: "mdiServer",
          title: this.$t("links.logs"),
          to: "/logs",
          valid: true,
          section: "main",
        },
        {
          icon: "notificationsBell",
          title: this.$t("links.notifications"),
          to: "/profile/notifications",
          valid: true,
          section: "main",
        },
        {
          icon: "groupIcon",
          title: this.$t("links.edit-groups"),
          to: "/groups/edit",
          valid: !this.isKioskSession,
          section: "manage",
        },
        {
          icon: "Plus",
          title: this.$t("links.new"),
          to: "/new-device/new",
          valid: !this.isKioskSession,
          section: "manage",
        },
        {
          icon: "mdiMap",
          title: this.$t("links.map"),
          to: "/map",
          valid: true,
          section: "manage",
        },
        {
          icon: "Router",
          title: this.$t("links.gateways"),
          to: "/gateways",
          valid: true,
          section: "manage",
        },
        {
          icon: "Auto",
          title: this.$t("links.automations"),
          to: "/automations",
          valid: this.$userPermissions.permitted("automations"),
          section: "automation",
        },
        {
          icon: "flashOutline",
          title: this.$t("links.solar-actions"),
          to: "/solar-actions",
          valid: this.hasSolarActionsDevices,
          section: "automation",
        },
        {
          icon: "mdiAccountCircle",
          title: this.$t("links.profile"),
          to: "/profile",
          valid: true,
          section: "account",
        },
        {
          icon: "settingsIcon",
          title: this.$t("links.settings"),
          to: "/profile/settings",
          valid: !this.isKioskSession,
          section: "account",
        },
        {
          icon: "mdiPower",
          title: this.$t("links.signout"),
          method: this.logout,
          valid: true,
          section: "account",
          isDanger: true,
        },
      ].filter((i) => i.valid);
    },
    groupedItems() {
      const isHe = this.locale === "he";
      const labels = {
        main: isHe ? "ראשי" : "Main",
        manage: isHe ? "ניהול" : "Manage",
        automation: isHe ? "אוטומציה" : "Automation",
        account: isHe ? "חשבון" : "Account",
      };
      return ["main", "manage", "automation", "account"]
        .map((key) => ({
          key,
          title: labels[key],
          items: this.items.filter((i) => (i.section || "main") === key),
        }))
        .filter((s) => s.items.length);
    },
  },
  created() {
    this.locale = lang.get();
  },
  methods: {
    logout() {
      this.u_logout = true;
    },
    goTo(path) {
      this.$router.push(path).catch(() => {});
    },
    isActive(item) {
      return !!item.to && this.path === item.to;
    },
  },
};
</script>

<style lang="scss" scoped>
/* ══════════════════════════════════════════════
   DRAWER SHELL — glassy dark panel
   ══════════════════════════════════════════════ */
:deep(.v-navigation-drawer.sidemenu-2027) {
  top: 0 !important;
  bottom: 0 !important;
  height: 100vh !important;
  min-height: 100dvh !important;
  background: linear-gradient(
    165deg,
    #16093a 0%,
    #1e0f46 40%,
    #0f1b3e 100%
  ) !important;
  backdrop-filter: blur(24px);
  box-shadow: 0 0 0 1px rgba(160, 100, 255, 0.18),
    0 32px 80px rgba(0, 0, 0, 0.55) !important;
  overflow: visible !important;
}

/* Light-mode override */
:deep(.v-navigation-drawer.sidemenu-2027):not(.sidemenu-2027--dark) {
  background: linear-gradient(
    165deg,
    #f3eeff 0%,
    #ede5ff 40%,
    #f8f5ff 100%
  ) !important;
  box-shadow: 0 0 0 1px rgba(122, 0, 204, 0.14),
    0 24px 60px rgba(60, 20, 120, 0.18) !important;
}

:deep(.v-navigation-drawer.sidemenu-2027 .v-navigation-drawer__border) {
  display: none;
}

/* ── Aurora blobs ── */
.aurora-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(55px);
  pointer-events: none;
  z-index: 0;
  animation: aurora-float 9s ease-in-out infinite alternate;
}

.aurora-blob--2 {
  width: 180px;
  height: 180px;
  bottom: 60px;
  inset-inline-end: -50px;
  background: radial-gradient(
    circle,
    rgba(0, 200, 220, 0.28) 0%,
    transparent 70%
  );
  animation-duration: 14s;
  animation-delay: -4s;
}

@keyframes aurora-float {
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 0.7;
  }
  50% {
    transform: translate(10px, -14px) scale(1.08);
    opacity: 0.9;
  }
  100% {
    transform: translate(-6px, 8px) scale(0.96);
    opacity: 0.6;
  }
}

/* ══════════════════════════════════════════════
   MENU SHELL
   ══════════════════════════════════════════════ */
.menu-shell {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* ── HEADER ── */
.menu-header {
  padding: 20px 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.sidemenu-2027:not(.sidemenu-2027--dark) .menu-header {
  border-bottom-color: rgba(100, 0, 200, 0.12);
}

.user-block {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: linear-gradient(135deg, #8b00ff 0%, #c459ff 100%);
  color: #fff;
  font-weight: 800;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.user-name {
  font-weight: 700;
  color: #fff;
  font-size: 0.97rem;
  line-height: 1.3;
}

.sidemenu-2027:not(.sidemenu-2027--dark) .user-name {
  color: #16093a;
}

.user-sub {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
  letter-spacing: 0.02em;
}

.sidemenu-2027:not(.sidemenu-2027--dark) .user-sub {
  color: rgba(80, 30, 140, 0.55);
}

.close-pill {
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.07);
  border-radius: 10px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.15s ease;
  backdrop-filter: blur(8px);
}

.close-pill:hover {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.25);
}

.sidemenu-2027:not(.sidemenu-2027--dark) .close-pill {
  border-color: rgba(100, 0, 200, 0.2);
  background: rgba(100, 0, 200, 0.06);
  color: rgba(60, 0, 120, 0.6);
}

/* ── BODY ── */
.menu-body {
  flex: 1;
  overflow-y: auto;
  padding: 10px 10px 6px;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 99px;
  }
}

/* Section chip */
.section-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
  padding: 10px 8px 4px;
}

.sidemenu-2027:not(.sidemenu-2027--dark) .section-chip {
  color: rgba(60, 0, 120, 0.4);
}

.section-chip-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: rgba(180, 100, 255, 0.55);
  display: inline-block;
}

/* Menu item */
.menu-item {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 0;
  background: transparent;
  border-radius: 13px;
  min-height: 44px;
  padding: 8px 10px;
  margin: 2px 0;
  color: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  font: inherit;
  text-align: inherit;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease,
    transform 0.15s ease;
  overflow: hidden;
}

.sidemenu-2027:not(.sidemenu-2027--dark) .menu-item {
  color: rgba(25, 10, 50, 0.78);
}

.menu-item:hover {
  background: rgba(160, 80, 255, 0.1);
  color: #fff;
  transform: translateX(-2px);
  box-shadow: 0 4px 16px rgba(140, 0, 255, 0.12);
}

.sidemenu-2027:not(.sidemenu-2027--dark) .menu-item:hover {
  color: #3a007a;
  background: rgba(122, 0, 204, 0.08);
  box-shadow: none;
}

.menu-item:active {
  transform: scale(0.97);
}

/* Active state */
.menu-item--active {
  background: linear-gradient(
    110deg,
    rgba(139, 0, 255, 0.38) 0%,
    rgba(160, 80, 255, 0.18) 100%
  );
  color: #fff;
  box-shadow: inset 0 0 0 1px rgba(200, 130, 255, 0.25),
    0 6px 18px rgba(139, 0, 255, 0.28);
}

.sidemenu-2027:not(.sidemenu-2027--dark) .menu-item--active {
  background: linear-gradient(
    110deg,
    rgba(122, 0, 204, 0.18) 0%,
    rgba(180, 120, 255, 0.1) 100%
  );
  color: #5a00b0;
  box-shadow: inset 0 0 0 1px rgba(122, 0, 204, 0.22),
    0 4px 14px rgba(122, 0, 204, 0.12);
}

/* Active glow bar (start edge — RTL-aware) */
.item-active-bar {
  position: absolute;
  top: 8px;
  bottom: 8px;
  inset-inline-start: 0;
  width: 3px;
  border-radius: 0 4px 4px 0;
  background: linear-gradient(180deg, #c084fc, #7c3aed);
  box-shadow: 0 0 8px rgba(192, 132, 252, 0.7);
  animation: active-bar-glow 2.4s ease-in-out infinite alternate;
}

@keyframes active-bar-glow {
  from {
    box-shadow: 0 0 6px rgba(192, 132, 252, 0.5);
  }
  to {
    box-shadow: 0 0 14px rgba(192, 132, 252, 0.95);
  }
}

/* Icon capsule */
.item-icon-wrap {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(200, 160, 255, 0.75);
  transition: all 0.2s ease;
}

.sidemenu-2027:not(.sidemenu-2027--dark) .item-icon-wrap {
  background: rgba(122, 0, 204, 0.07);
  border-color: rgba(122, 0, 204, 0.12);
  color: rgba(90, 0, 160, 0.65);
}

.menu-item--active .item-icon-wrap {
  background: rgba(192, 100, 255, 0.22);
  border-color: rgba(220, 160, 255, 0.3);
  color: #e0b4ff;
  box-shadow: 0 0 10px rgba(192, 132, 252, 0.4);
}

.menu-item:hover .item-icon-wrap {
  background: rgba(160, 80, 255, 0.15);
  color: #d4a4ff;
}

/* Item label */
.item-label {
  font-weight: 600;
  font-size: 0.92rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Danger (logout) */
.menu-item--danger {
  color: rgba(255, 120, 120, 0.72);
  margin-top: 4px;
}

.menu-item--danger .item-icon-wrap {
  background: rgba(255, 60, 60, 0.1);
  border-color: rgba(255, 80, 80, 0.15);
  color: rgba(255, 120, 120, 0.75);
}

.menu-item--danger:hover {
  color: #ff9090;
  background: rgba(255, 60, 60, 0.12);
  box-shadow: 0 4px 16px rgba(255, 60, 60, 0.12);
}

/* ── FOOTER ── */
.menu-footer {
  padding: 10px 10px 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sidemenu-2027:not(.sidemenu-2027--dark) .menu-footer {
  border-top-color: rgba(100, 0, 200, 0.1);
}

.locale-glass {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 0 4px;
  backdrop-filter: blur(12px);
}

.sidemenu-2027:not(.sidemenu-2027--dark) .locale-glass {
  background: rgba(122, 0, 204, 0.05);
  border-color: rgba(122, 0, 204, 0.14);
}
</style>
